import { useEffect, useMemo } from "react";
import { Box } from "../../primitives/box";
import type { ShimmerProps } from "./types";
import { useShimmerStyles } from "./styles";

let shimmerStylesInjected = false;

function ensureShimmerStyles() {
  if (shimmerStylesInjected) {
    return;
  }
  if (typeof document === "undefined") {
    return;
  }
  const styleElement = document.createElement("style");
  styleElement.textContent = `
    @keyframes shimmer-horizontal {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes shimmer-vertical {
      0% { background-position: 0 200%; }
      100% { background-position: 0 -200%; }
    }
    @keyframes shimmer-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes shimmer-wave {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .shimmer-horizontal {
      animation-name: shimmer-horizontal;
    }
    .shimmer-vertical {
      animation-name: shimmer-vertical;
    }
    .shimmer-pulse {
      animation-name: shimmer-pulse;
    }
    .shimmer-wave {
      animation-name: shimmer-wave;
    }
    @media (prefers-reduced-motion: reduce) {
      .shimmer-horizontal,
      .shimmer-vertical,
      .shimmer-pulse,
      .shimmer-wave {
        animation: none;
        background-position: 0 0;
      }
    }
  `;
  document.head.appendChild(styleElement);
  shimmerStylesInjected = true;
}

export const Shimmer = ({
  width,
  height,
  shape = "rectangle",
  animationType = "horizontal",
  animated = true,
  duration = 1500,
  delay = 0,
  baseColor,
  highlightColor,
  borderRadius = 0,
  variant,
  aspectRatio,
  accessibilityLabel = "Loading...",
  style,
  ...rest
}: ShimmerProps) => {
  useEffect(() => {
    ensureShimmerStyles();
  }, []);

  const styles = useShimmerStyles({
    width,
    height,
    shape,
    animationType,
    animated,
    duration,
    delay,
    baseColor,
    highlightColor,
    borderRadius,
    variant,
  });

  const animationClass = useMemo(() => {
    if (!animated) return "";
    return `shimmer-${animationType}`;
  }, [animated, animationType]);

  const containerStyle = useMemo(() => {
    return {
      ...styles.container,
      ...(aspectRatio && { aspectRatio }),
      ...style,
    };
  }, [styles.container, aspectRatio, style]);

  const boxProps = {
    as: "div" as const,
    className: animationClass,
    style: containerStyle,
    role: "status" as const,
    "aria-label": accessibilityLabel,
    "aria-live": "polite" as const,
    ...rest,
  };

  return <Box {...(boxProps as React.ComponentProps<typeof Box>)} />;
};

Shimmer.displayName = "Shimmer";
